import _AidsPanelTranslateMutation from "../__generated__/AidsPanelTranslateMutation.graphql";
import _AidsPanelTranslationQuery from "../__generated__/AidsPanelTranslationQuery.graphql";
import _AidsPanelSpeakMutation from "../__generated__/AidsPanelSpeakMutation.graphql";
import { Languages, Loader2, Volume2, X } from 'lucide-react';
import { Component, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { getLocale, t } from '../i18n/t';
import { En } from '../i18n/En';
import { languageName } from '../profile/languages.ts';
import { codeFromError, copyFor } from './aidErrors.ts';
import { isSpeakable, type Aid, type HearableCorrection } from './aids.ts';
import type { AidsPanelSpeakMutation } from '../__generated__/AidsPanelSpeakMutation.graphql';
import type { AidsPanelTranslateMutation } from '../__generated__/AidsPanelTranslateMutation.graphql';
import type { AidsPanelTranslationQuery } from '../__generated__/AidsPanelTranslationQuery.graphql';
export const Speak = (_AidsPanelSpeakMutation.hash && _AidsPanelSpeakMutation.hash !== "31002ee1106833567d239db0e7af9530" && console.error("The definition of 'AidsPanelSpeakMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AidsPanelSpeakMutation);
const Translation = (_AidsPanelTranslationQuery.hash && _AidsPanelTranslationQuery.hash !== "9a14004b8032a3a8dd800c4df530ae1d" && console.error("The definition of 'AidsPanelTranslationQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AidsPanelTranslationQuery);
const TranslateTurn = (_AidsPanelTranslateMutation.hash && _AidsPanelTranslateMutation.hash !== "3a8ce6009644b6c90fcccb1e402cf0e4" && console.error("The definition of 'AidsPanelTranslateMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _AidsPanelTranslateMutation);

/**
 * Catches a thrown `useLazyLoadQuery` the way `Suspense` beside it catches a
 * pending one. The app has no boundary anywhere else (`HubDetail`'s
 * `ActivityFeed` has the same exposure), but this one sits on a live
 * conversation: without it, a failed translation fetch unmounts the whole
 * session, not just the panel the learner opened on a line. Scoped to just
 * `TranslateSection` rather than the whole modal, so the header's close
 * button and the speak section -- neither of which can throw here -- keep
 * working even if this one section can't load.
 */
export class TranslationBoundary extends Component<{
  children: ReactNode;
}, {
  failed: boolean;
}> {
  state = {
    failed: false
  };
  static getDerivedStateFromError() {
    return {
      failed: true
    };
  }
  render() {
    if (this.state.failed) {
      return <p role="alert" className="text-error text-xs">
          {t(copyFor(null))}
        </p>;
    }
    return this.props.children;
  }
}

/**
 * The translation half of the panel, its own component so `useLazyLoadQuery`
 * can sit behind a `Suspense` boundary scoped to just this section — the
 * "Hear it again" button beside it has nothing to wait on and must not be
 * held up by this section's round trip.
 *
 * `Turn.translation` is read first because, per its own doc comment, that is
 * what the field is for: asking whether a translation already exists costs
 * nothing, where `translateTurn` spends the rate-limited budget a model call
 * costs. This is that ordering, not a cache this component invents.
 */
export function TranslateSection({
  turnId,
  language,
  audio
}: {
  turnId: string;
  language: string;
  /**
   * Shared with `AidsPanel`'s own "Hear it again" button, so a tap here stops
   * that playback rather than layering a second voice over it — the same
   * "a second click has to stop the first line before starting itself"
   * property that button already keeps for repeated clicks of its own.
   */
  audio: React.RefObject<HTMLAudioElement | null>;
}) {
  const data = useLazyLoadQuery<AidsPanelTranslationQuery>(Translation, {
    turnId,
    language
  });
  const stored = data.node?.translation ?? null;
  // What `translateTurn` just returned, kept beside `stored` rather than
  // relied on to update it: Relay's default `getDataID` (no custom one is
  // installed in `relayEnv.ts`) does normalise both records under
  // `Translation.id`, so the mutation response and a later query for the
  // same row do become one record in the store -- but `Turn.translation
  // (language:)` is a *linked field* on the `Turn` record, and normalising
  // the `Translation` record elsewhere does not repoint that link. The link
  // itself was already written as `null` by the query this component ran
  // before translating, and nothing here rewrites it.
  //
  // The consequence: reopening this same line later re-runs the query with
  // the default `store-or-network` policy, which -- because that exact
  // query's own cached response already resolved the link to `null` --
  // serves that cached `null` without a network round trip, and the learner
  // sees "Translate" again for a line they already translated. Left alone
  // on purpose -- the retry costs a round trip, not a model call, since
  // `translateTurn` returns the already-stored row rather than asking oMLX
  // again -- rather than reaching for `network-only` or a store updater to
  // repoint the link and chase a correctness gap nobody
  // pays for.
  const [justTranslated, setJustTranslated] = useState<{
    id: string;
    language: string;
    text: string;
  } | null>(null);
  const [translate, translating] = useMutation<AidsPanelTranslateMutation>(TranslateTurn);
  const [error, setError] = useState<string | null>(null);
  const translation = justTranslated ?? stored;
  const onTranslate = useCallback(() => {
    setError(null);
    translate({
      variables: {
        turnId
      },
      onCompleted: response => setJustTranslated(response.translateTurn.translation),
      // Every refusal here — a spent rate limit, an unusable stored language,
      // oMLX down — comes through this one callback (see `aidErrors.ts`'s doc
      // comment for why), so the copy has to be chosen from what the server
      // actually said, not assumed to be a dropped connection.
      onError: err => setError(t(copyFor(codeFromError(err))))
    });
  }, [translate, turnId]);

  // §5: "the translation runs the other way, and the target form can be
  // heard." This is the half of that sentence nothing in the project could
  // deliver until `speak` grew a `turnAidId` branch -- naming the stored row
  // rather than the turn it was made from, the same "a row this account
  // owns, never a string" shape `turnId` and `errorRecordId` already have.
  const [speakTranslation, speakingTranslation] = useMutation<AidsPanelSpeakMutation>(Speak);
  const [speakTranslationError, setSpeakTranslationError] = useState<string | null>(null);
  const playTranslation = useCallback((turnAidId: string) => {
    setSpeakTranslationError(null);
    audio.current?.pause();
    speakTranslation({
      variables: {
        input: {
          turnAidId
        }
      },
      onCompleted: ({
        speak: result
      }) => {
        // Paused again here, not only at click time: `AidsPanel`'s own
        // "Hear it again" shares this same ref, and its mutation can
        // finish *after* this one starts playing (both are in flight at
        // once whenever a learner taps this button before that one's
        // round trip returns). Without this, whichever call resolves
        // second overwrites `audio.current` without stopping what the
        // other one already started -- two voices layered, the one
        // outcome sharing the ref exists to prevent.
        audio.current?.pause();
        const el = new Audio(`data:audio/wav;base64,${result.wavBase64}`);
        audio.current = el;
        // `audio.current === el` guards against the pause two lines above
        // being what rejects this: if the *other* button's `onCompleted`
        // runs between this assignment and `play()` resolving, its own
        // pause of `el` (to take over the shared ref) rejects this promise
        // too (`AbortError`) even though this line's own clip did play --
        // without the guard, that shows this button's error under a clip
        // that succeeded.
        void el.play().catch(() => {
          if (audio.current === el) setSpeakTranslationError(t(copyFor(null)));
        });
      },
      onError: err => setSpeakTranslationError(t(copyFor(codeFromError(err))))
    });
  }, [speakTranslation, audio]);
  if (translation !== null) {
    return <div className="flex flex-col gap-1">
        {/* The row's own language, not the `language` prop: that prop is
            `Transcript`'s client-known `translationTarget`, taken at the
            moment the panel opened, and can go stale (a native language
            changed in another tab, then this account translates the same
            turn again) in a way that produces a *correct* translation under
            a *wrong* heading -- worse than visibly stale, since nothing
            about it looks wrong. `Translation.language` is what the server
            actually made this row into and exists precisely so a caller
            never has to guess. */}
        <p className="text-base-content/50 text-xs">
          {t('aids-in-language', {
          language: languageName(translation.language, getLocale())
        })}
        </p>
        <p>
          <span dir="auto">{translation.text}</span>
        </p>
        {/* Offered only for a translation the cast can actually say -- in
            practice §5's "Say this in English" rendering, since every
            character speaks with a Kokoro English voice and the profile's
            other languages have no voice at all. Asking for Vietnamese does
            not fail loudly: `af_heart` reads it as though the letters were
            English, so the learner is given a confident mispronunciation of
            the one thing they opened this panel to learn. `isSpeakable` is
            asked of `Translation.language` -- the language the server made
            this row into -- rather than of the client's `translateInto`,
            which can be stale for the same reason the heading above uses the
            stored value. */}
        {isSpeakable(translation.language) && <button type="button" className="btn btn-outline btn-sm min-h-(--hit-min) w-fit gap-2" disabled={speakingTranslation} onClick={() => playTranslation(translation.id)}>
            {speakingTranslation ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Volume2 aria-hidden className="size-4" />}
            {speakingTranslation ? t('common-loading') : t('aids-hear-it')}
          </button>}
        {speakTranslationError && <p role="alert" className="text-error text-xs">
            {speakTranslationError}
          </p>}
      </div>;
  }
  return <div className="flex flex-col gap-1">
      <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2" disabled={translating} onClick={onTranslate}>
        {translating ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Languages aria-hidden className="size-4" />}
        {translating ? t('aids-translating') : t('aids-translate-into', {
        language: languageName(language, getLocale())
      })}
      </button>
      {error && <p role="alert" className="text-error text-xs">
          {error}
        </p>}
    </div>;
}

/**
 * What a learner opens on a line they did not follow: a translation into
 * their own language, the line spoken aloud again, and -- §5 -- that
 * translation spoken aloud too, once it exists.
 *
 * A modal rather than a drawer, matching `HubDetail`'s idiom exactly —
 * `modal-open`, a `modal-box`, and a sibling `modal-backdrop` button as
 * daisyUI's own click-outside — so the app has one overlay pattern, not two.
 *
 * `aids` is computed once by the caller (`Transcript`), which already knows
 * `who` and the learner's native language for every line on screen; this
 * component only renders what it is told to offer.
 *
 * The caller keys this by turn id (`Transcript.tsx`), which is what makes a
 * fresh mount — and fresh local state — the actual guarantee when the
 * learner switches lines. The focus trap below is the second half of that:
 * without it, focus stays on the trigger button behind the panel, one Tab
 * reaches the *next* line's trigger (still in the transcript's own tab
 * order, since nothing here has moved focus or hidden it), and Enter
 * re-targets this panel at a different turn without the learner ever having
 * chosen to close it.
 */
export function AidsPanel({
  turnId,
  text,
  aids,
  translateInto,
  corrections = [],
  onClose
}: {
  turnId: string;
  text: string;
  aids: readonly Aid[];
  /**
   * What `translateTurn` will put this line into — the server's own
   * `Turn.translationTarget` (or the matching field on the live commit
   * event), read straight from `Transcript`'s `Line` rather than derived
   * here. §5: it is `"English"` on the one line `aidsFor`'s `sayInEnglish`
   * fires for, the learner's native language otherwise.
   *
   * **Can be `null` even when `aids` contains `'translate'`.** That aid is
   * gated on `hasNativeLanguage` alone (`Transcript.tsx` passes
   * `nativeLanguage != null`, nothing about this field), so a learner whose
   * stored native language resolves to English (a row written before ruling
   * Y's refusal existed) still gets `'translate'` in `aids` but `null` here.
   * The render below checks both (`aids.includes('translate') &&
   * translateInto`) — that check, not the type of `aids`, is what actually
   * decides whether this section renders.
   */
  translateInto: string | null;
  /** From `correctionsFor`: empty for a child, a tutor line, or a clean line. */
  corrections?: readonly HearableCorrection[];
  onClose: () => void;
}) {
  const [speak, speaking] = useMutation<AidsPanelSpeakMutation>(Speak);
  const [speakError, setSpeakError] = useState<string | null>(null);
  // Its own line, so a failed correction does not report under "Hear it
  // again", which the learner did not press.
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  // The audio element outlives any single render, the same way
  // `CharacterSelect`'s preview player does — a second click has to stop the
  // first line before starting itself, not layer both. Shared with
  // `TranslateSection`'s own "Hear it" button for the same reason: a learner
  // who taps both must hear one voice at a time, not two layered.
  const audio = useRef<HTMLAudioElement | null>(null);
  const box = useRef<HTMLDivElement | null>(null);

  // Closing the panel mid-playback must not leave a line still audible after
  // its own controls are gone.
  useEffect(() => () => audio.current?.pause(), []);

  // Move focus into the panel on mount (once per turn, since the caller
  // remounts this on `key={turnId}`), so a learner tabbing after opening it
  // moves inside the panel rather than through the transcript behind it.
  useEffect(() => {
    box.current?.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]')?.focus();
  }, []);

  // A minimal Tab trap scoped to the panel's own content, queried fresh on
  // every keystroke rather than once: the translate button doesn't exist
  // until its `Suspense` resolves, and caching the focusable set at mount
  // would trap focus against a set that's already stale by the time it
  // matters. The backdrop button is deliberately outside this trap -- it is
  // a pointer affordance for click-outside, and the header's `X` already
  // covers closing by keyboard.
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !box.current) return;
    const focusable = box.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);
  const hear = useCallback((input: AidsPanelSpeakMutation['variables']['input'], setError: (e: string | null) => void) => {
    setSpeakError(null);
    setCorrectionError(null);
    audio.current?.pause();
    speak({
      variables: {
        input
      },
      onCompleted: ({
        speak: result
      }) => {
        // Paused again here, not only at click time -- see the matching
        // comment in `TranslateSection`'s `playTranslation`: this call and
        // that one can both be in flight together, and whichever resolves
        // second must stop whatever the other already started rather than
        // layering over it.
        audio.current?.pause();
        const el = new Audio(`data:audio/wav;base64,${result.wavBase64}`);
        audio.current = el;
        // `audio.current === el` guards against the pause two lines above --
        // see the matching comment in `TranslateSection`'s
        // `playTranslation` -- being what rejects this: the other button's
        // `onCompleted` pausing `el` to take over the shared ref must not
        // show this button's error under a clip that actually played.
        void el.play().catch(() => {
          if (audio.current === el) setError(t(copyFor(null)));
        });
      },
      onError: err => setError(t(copyFor(codeFromError(err))))
    });
  }, [speak]);
  const play = useCallback(() => hear({
    turnId
  }, setSpeakError), [hear, turnId]);
  return <div className="modal modal-open" role="dialog">
      <div className="modal-box flex max-w-lg flex-col gap-4" ref={box} onKeyDown={onKeyDown}>
        <header className="flex items-start justify-between gap-2">
          <p className="text-base-content/70 line-clamp-2 text-sm italic">“{text}”</p>
          {/* `size-(--hit-min)`, not `min-h-(--hit-min)`: daisyUI 5.7.36 sets
              both width and height of a `btn-circle` from `--size` (32px for
              `btn-sm`), so a min-height alone left this 32px wide and 64px
              tall in the kid register -- still under the hit floor on one
              axis, and no longer a circle. `size-*` overrides both, the same
              way `min-h-(--hit-min)` already overrides `btn-sm`'s height on
              every other control in this file. */}
          <button className="btn btn-ghost btn-circle size-(--hit-min)" aria-label={t('aids-close')} onClick={onClose}>
            <X aria-hidden className="size-4" />
          </button>
        </header>

        {aids.includes('speak') && <div className="flex flex-col gap-1">
            <button type="button" className="btn btn-outline min-h-(--hit-min) gap-2" disabled={speaking} onClick={play}>
              {speaking ? <Loader2 aria-hidden className="size-4 animate-spin" /> : <Volume2 aria-hidden className="size-4" />}
              {speaking ? t('common-loading') : t('aids-hear-again')}
            </button>
            {speakError && <p role="alert" className="text-error text-xs">
                {speakError}
              </p>}
          </div>}

        {corrections.length > 0 && <div className="flex flex-col gap-1">
            <p className="text-base-content/50 text-xs">{t('aids-hear-correction')}</p>
            {corrections.map(c => <button key={c.id} type="button" className="btn btn-ghost min-h-(--hit-min) justify-start gap-2" disabled={speaking} onClick={() => hear({
          errorRecordId: c.id
        }, setCorrectionError)}>
                <Volume2 aria-hidden className="size-4" />
                <En>{c.targetForm}</En>
              </button>)}
            {correctionError && <p role="alert" className="text-error text-xs">
                {correctionError}
              </p>}
          </div>}

        {aids.includes('translate') && translateInto && <TranslationBoundary>
            <Suspense fallback={<p className="text-base-content/50 text-sm">{t('aids-loading-translation')}</p>}>
              <TranslateSection turnId={turnId} language={translateInto} audio={audio} />
            </Suspense>
          </TranslationBoundary>}
      </div>
      <button type="button" className="modal-backdrop" aria-label={t('aids-close')} onClick={onClose} />
    </div>;
}